import React, { useCallback, useEffect } from 'react';
import clsx from 'clsx';
import CssBaseline from '@material-ui/core/CssBaseline';
import { useTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import Divider from '@material-ui/core/Divider';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { SideMenu } from './components/Side-menu.js';
import Toolbar from "@material-ui/core/Toolbar";
import AppBar from "@material-ui/core/AppBar";
import Container from "@material-ui/core/Container";
import Button from "@material-ui/core/Button";
import Typography from '@material-ui/core/Typography';

import { config } from './utils/config';
import { useStyles } from './utils/style';
import UrlParams from './utils/UrlParams';
import Utils from './utils/utils';

import './css/App.css';

let { SRGLetterbox } = window;
let srgLetterboxPlayer = null;

/**
 * APP HOOKS
 *
 * @returns {HTML}
 * @constructor
 */
export default function App() {
    const theme = useTheme();
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [version, setVersion] = React.useState('Loading...');
    const [urnValue, setUrnValue] = React.useState(UrlParams.get('urn') || 'urn:rts:video:10719425');
    const [envValue, setEnvValue] = React.useState('il.srgssr.ch');
    const [langValue, setLangValue] = React.useState(UrlParams.get('lang') || 'en');
    const [playerValue, setPlayer] = React.useState(UrlParams.get('player') || 'stage');

    const playerBaseUrl = useCallback(() => {
        if (playerValue === 'local') {
            return 'http://localhost:1234';
        } else {
            return `//letterbox-web-2.s3.amazonaws.com/${playerValue}`;
        }
    }, [playerValue]);

    const initializePlayer = useCallback(() => {
        const optionsParam = {
            debug: true,
            ilHost: envValue,
            fillMode: true,
            playerFocus: true,
            hdMode: undefined,
            language: langValue,
        };

        SRGLetterbox = window.SRGLetterbox;
        
        setVersion(SRGLetterbox.LONG_VERSION);
        
        srgLetterboxPlayer = new SRGLetterbox(optionsParam);
        srgLetterboxPlayer.initializeOnElement('letterboxWebPlayer');
        srgLetterboxPlayer.player.fluid(true);
        srgLetterboxPlayer.player.fill();
        srgLetterboxPlayer.prepareToPlayURN(urnValue);
        
        UrlParams.set('lang', langValue);
        UrlParams.set('env', envValue);
    }, [envValue, langValue, urnValue]);

    const mountPlayer = useCallback(() => {
        const script = document.createElement('script');

        script.async = true;
        script.src = `${playerBaseUrl()}/letterbox.js`;
        script.type = 'text/javascript';

        script.onload = initializePlayer;

        document.body.appendChild(script);

        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = `${playerBaseUrl()}/letterbox.css`;
        link.media = 'all';

        document.head.appendChild(link);
        UrlParams.set('player', playerValue);
    }, [playerBaseUrl, initializePlayer, playerValue]);

    const unmountPlayer = () => {
        setVersion('Loading...');
        srgLetterboxPlayer.player.dispose();
        Utils.removeFile('letterbox.js', 'js');
        Utils.removeFile('letterbox.css', 'css');
    };

    /**
     * When the component is mounted, we need to mount the player
     */
    useEffect(() => {
        mountPlayer();
        return () => {
            unmountPlayer();
        };
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (srgLetterboxPlayer) {
            unmountPlayer();
            mountPlayer();
        }
        //eslint-disable-next-line
    }, [langValue, playerValue, envValue]);

    useEffect(() => {
        if (srgLetterboxPlayer) {
            srgLetterboxPlayer.prepareToPlayURN(urnValue);
            UrlParams.set('urn', urnValue);
        }
    }, [urnValue]);
    /**
     * Handlers
     */
    function handleDrawerOpen() {
        setOpen(true);
    }

    function handleDrawerClose() {
        setOpen(false);
    }

    function handleMediaComposition() {
        const url = `http://${envValue}/integrationlayer/2.0/mediaComposition/byUrn/${urnValue}.json`;
        window.open(encodeURI(url), "Mediacomposition", '_blank');
    }

    function handlePlayerDocumentation() {
        const url = `${playerBaseUrl()}/docs/index.html`;
        window.open(encodeURI(url), "Player API", '_blank');
    }

    /**
     * Render
     */
    return (
        <React.Fragment>
            <div className={classes.root}>
                <CssBaseline />
                <AppBar position="fixed" className={clsx(classes.appBar, {
                    [classes.appBarShift]: open,
                })}>
                    <Toolbar className={classes.toolbar}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="Open drawer"
                            onClick={handleDrawerOpen}
                            className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" className={classes.title}>
                            {config.header.title}
                        </Typography>
                        <Button color="inherit" target="_blank"
                            href="https://github.com/SRGSSR/srgletterbox-web/">Git</Button>
                    </Toolbar>
                </AppBar>
                <Drawer
                    className={classes.drawer}
                    variant="persistent"
                    anchor="left"
                    open={open}
                    classes={{
                        paper: classes.drawerPaper,
                    }}
                >
                    <div className={classes.drawerHeader}>
                        <IconButton onClick={handleDrawerClose}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </div>
                    <Divider />
                    <SideMenu datas={config.menu}
                        initURN={urnValue}
                        initEnv={envValue}
                        initLang={langValue}
                        initPlayer={playerValue}
                        callbackURN={setUrnValue}
                        callbackMediaCompo={handleMediaComposition}
                        callbackPlayerDocumentation={handlePlayerDocumentation}
                        callbackEnv={setEnvValue}
                        callbacklang={setLangValue}
                        callbackPlayer={setPlayer} />
                </Drawer>
                <main className={clsx(classes.content, {
                    [classes.contentShift]: open,
                })}>
                    <div className={classes.drawerHeader} />
                    <Container maxWidth="xl">
                        <div id={'letterboxWebPlayer'} className={classes.rootVideoPlayer}></div>
                        <Typography variant="subtitle2" className={classes.playerVersion}>
                            {config.header.description}{version}
                        </Typography>
                    </Container>

                </main>
            </div>
        </React.Fragment>
    );
}
